// Create or rename a partner. The smallest form in the app, and the template
// for the other seven: a draft of strings, one `toWire` that turns it into the
// contract's shape, and the shared editor for everything after that.
//
// Mounted only while open (the parent renders it conditionally), because the
// draft and the version it locks onto are read once, at mount — the version
// `If-Match` carries has to be the one the user was looking at.

import type { ReactNode } from "react";
import { PartnerCreate, PartnerUpdate } from "@crm/shared";
import type { Partner } from "@crm/shared";
import { usePartnerMutations } from "../../api/mutations.js";
import { usePartnerOptions } from "../../api/queries.js";
import { ProblemMessage } from "../../design/Feedback.js";
import { FormDialog } from "../../forms/FormDialog.js";
import { ColorField, SelectField, TextField, type SelectOption } from "../../forms/fields.js";
import { optionalText } from "../../forms/parseInput.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";
import { eligibleParents } from "./partnerTree.js";

interface PartnerDraft {
  name: string;
  parent_id: string;
  notes: string;
  /** `#rrggbb`, or "" for none. */
  color: string;
}

const BLANK: PartnerDraft = {
  name: "",
  parent_id: "",
  notes: "",
  color: "",
};

const LABELS = {
  name: "Partner name",
  parent_id: "Owned by",
  notes: "Notes",
  color: "Colour",
} as const;

function draftFromRecord(partner: Partner): PartnerDraft {
  return {
    name: partner.name,
    // A select holds strings, so "no owner" is the empty option — `toWire`
    // turns it back into the null the contract means by top-level.
    parent_id: partner.parent_id ?? "",
    notes: partner.notes ?? "",
    color: partner.color ?? "",
  };
}

/**
 * Trimmed HERE as well as by the schema, so that adding a trailing space and
 * saving isn't seen as an edit and sent as a no-op PATCH.
 */
function toWire(draft: PartnerDraft): Wire {
  return {
    name: draft.name.trim(),
    parent_id: draft.parent_id === "" ? null : draft.parent_id,
    notes: optionalText(draft.notes),
    color: draft.color === "" ? null : draft.color,
  };
}

/**
 * The owners this partner may be moved under. Everything beneath it is gone
 * from the list (see `eligibleParents`), and its CURRENT owner is added back
 * from the record when the fetched list has not arrived yet — otherwise a
 * select whose value matches no option falls back to its blank first entry and
 * tells the reader, on screen, that this partner has no owner.
 */
function ownerOptions(
  partners: readonly Partner[],
  partner: Partner | undefined,
): readonly SelectOption<string>[] {
  const eligible = eligibleParents(partners, partner?.id ?? null);
  const options = eligible.map((candidate) => ({ value: candidate.id, label: candidate.name }));

  if (partner === undefined || partner.parent_id === null || partner.parent_name === null) {
    return options;
  }
  if (options.some((option) => option.value === partner.parent_id)) return options;
  return [{ value: partner.parent_id, label: partner.parent_name }, ...options];
}

export interface PartnerFormDialogProps {
  /** Absent for a new partner. */
  partner?: Partner;
  onClose: () => void;
  onSaved?: (partner: Partner) => void;
}

export function PartnerFormDialog({
  partner,
  onClose,
  onSaved,
}: PartnerFormDialogProps): ReactNode {
  const mutations = usePartnerMutations();
  const partners = usePartnerOptions();
  const isNew = partner === undefined;

  const editor = useEditor<PartnerDraft, PartnerCreate, PartnerUpdate, Partner>({
    mode: partner === undefined ? { kind: "create" } : { kind: "update", record: partner },
    blankDraft: BLANK,
    draftFromRecord,
    toWire,
    createSchema: PartnerCreate,
    updateSchema: PartnerUpdate,
    create: (body, idempotencyKey) => mutations.create.mutateAsync({ body, idempotencyKey }),
    update: (id, body, version) => mutations.update.mutateAsync({ id, body, version }),
    fetchCurrent: mutations.fetchCurrent,
    onSaved: (saved) => {
      onSaved?.(saved);
      onClose();
    },
  });

  return (
    <FormDialog
      open
      onClose={onClose}
      title={isNew ? "New partner" : "Edit partner"}
      submitLabel={isNew ? "Create partner" : "Save changes"}
      editor={editor}
      labels={LABELS}
    >
      <TextField
        label={LABELS.name}
        required
        value={editor.draft.name}
        error={editor.errors["name"]}
        onChange={(value) => editor.setField("name", value)}
      />

      {partners.error !== null && (
        <ProblemMessage error={partners.error} action="load the list of possible owners" />
      )}

      <SelectField
        label={LABELS.parent_id}
        placeholder="No owner (top level)"
        value={editor.draft.parent_id}
        options={ownerOptions(partners.data?.items ?? [], partner)}
        error={editor.errors["parent_id"]}
        hint="Deals under this partner inherit the owner's default agreement unless they pin one of their own, and the owner's reports can be run over this company too."
        onChange={(value) => editor.setField("parent_id", value)}
      />

      <ColorField
        label={LABELS.color}
        hint="Drawn beside this partner's name on the deals board and lists, so its deals can be told apart at a glance."
        value={editor.draft.color}
        error={editor.errors["color"]}
        onChange={(value) => editor.setField("color", value)}
      />

      <TextField
        label={LABELS.notes}
        multiline
        hint="Anything a person reading this partner's deals would want to know. Searchable."
        value={editor.draft.notes}
        error={editor.errors["notes"]}
        onChange={(value) => editor.setField("notes", value)}
      />
    </FormDialog>
  );
}
